<template>
    <CompassMenu
        placement="top-start"
        trigger="method"
        customClass="attachment-richtext__menu"
        :disabled="disabled"
        :isOpen="showAttachmentMenu"
        @open="showAttachmentMenu = true"
        @close="showAttachmentMenu = false"
    >
        <CompassTooltip
            :content="getExtensionOptionLabel()"
            :delay="{ show: 0, hide: 1 }"
            :disabled="disabled"
            placement="top"
        >
            <CompassIconButton
                ref="attachmentRichTextTrigger"
                :icon="getExtensionOptionIcon()"
                :disabled="disabled && loadingAttachment"
                :active="activeStyle(option)"
                showDropdown
                size="small"
                @click="showAttachmentMenu = !showAttachmentMenu"
            />
        </CompassTooltip>

        <template #dropdown>
            <CompassMenuItem
                :label="
                    $t.__('richtext.customoptions.AttachmentRichText.add_attachment_from_sellsy')
                "
                prefixIcon="folder"
                @click="displaySelectFileDropdown"
            />
            <CompassMenuItem
                :label="
                    $t.__('richtext.customoptions.AttachmentRichText.add_attachment_from_device')
                "
                prefixIcon="download"
                @click="selectFilesFromComputer"
            />
        </template>

        <input
            ref="fileInput"
            type="file"
            style="display: none"
            multiple
            @change="handleFileChange"
        />

        <CompassDropdown
            v-click-outside="handleClickOutside"
            parentRef="attachmentRichTextTrigger"
            dropdownClass="attachment-richtext__dropdown"
            placement="top-start"
            :isVisible="showSelectFilesDropdown"
            :actions="selectFilesDropdownActions"
            :filterablePlaceholder="$t.__('common.actions.search')"
            filterable
            isAutoComplete
            :noResults="files.length === 0"
            :results="files"
            :noResultsLabel="selectFilesDropdownNoResultsLabel"
            :loading="isLoadingFiles"
            @filter="searchFilesFromSellsy"
        >
            <CompassDropdownOption
                v-for="file in files"
                :key="file.id"
                :value="file.id"
                :label="getFileName(file)"
                :caption="getFileSize(file.size)"
                @itemSelected="selectFileFromSellsy(file)"
            >
                <div class="attachment-richtext__dropdown-option">
                    <svg
                        class="attachment-richtext__dropdown-option-icon"
                        :viewBox="getFileIconViewBox(file.extension)"
                        v-html="getFileIcon(file.extension)"
                    ></svg>
                    <span
                        class="attachment-richtext__dropdown-option-label typographies-paragraph-medium-regular"
                    >
                        {{ getFileName(file) }}
                    </span>
                </div>
            </CompassDropdownOption>
        </CompassDropdown>
    </CompassMenu>
</template>

<script>
import OptionsRichTextMixin from 'mixins/OptionsRichTextMixin';
import { searchFiles } from 'api/file/fileService';
import { icons } from 'vcomponents/email/EmailAttachmentFileTypes';
import { acceptedFileTypes } from 'Config/authenticated/dictionary/emailAttachments';
import mime from 'mime';

export default {
    mixins: [OptionsRichTextMixin],
    props: {
        hash: {
            type: String,
            default: '',
        },
        attachmentEndpoint: {
            type: String,
            default: '',
        },
    },
    data() {
        return {
            loadingAttachment: false,
            showAttachmentMenu: false,
            showSelectFilesDropdown: false,
            files: [],
            selectFilesDropdownActions: [
                {
                    label: this.$t.__('common.actions.back'),
                    callback: () => {
                        this.showAttachmentMenu = true;
                        this.showSelectFilesDropdown = false;
                    },
                },
            ],
            isLoadingFiles: false,
            filesLoadedOnce: false,
            selectFilesDropdownNoResultsLabel: this.$t.__('common.labels.no_result'),
        };
    },
    methods: {
        getExtensionOptionLabel() {
            return this.$t.__(this.getExtensionOption(this.option).label);
        },
        getExtensionOptionIcon() {
            return this.getExtensionOption(this.option).icon;
        },
        // Select a file from the computer
        selectFilesFromComputer() {
            this.$refs.fileInput.click();
        },
        // Handle the file change (when a file is selected)
        async handleFileChange(event) {
            this.loadingAttachment = true;
            const files = event.target.files;

            for (const file of files) {
                const formData = new FormData();
                formData.append('file', file);
                const formattedFile = formData;
                let fileDetails = {};

                if (file) {
                    fileDetails = {
                        name: file.name,
                        size: file.size,
                        content_type: file.type,
                        content_id: file.name,
                        extension: '',
                        isFileLoaded: true,
                    };
                    await EventBus.emit(`richText-${this.hash}:loadingAttachments`, fileDetails);
                }

                const endpoint =
                    this.attachmentEndpoint !== '' ? this.attachmentEndpoint : '/email/attachment';

                ApiClient.post(endpoint, {}, formattedFile)
                    .then((response) => {
                        if (response.data) {
                            const fileInfo = response.data;
                            const parsedFile = {
                                name: fileInfo.name,
                                size: fileInfo.size,
                                extension: fileInfo.extension,
                                content_id: fileInfo.content_id || fileInfo.id,
                                content_type: fileInfo.content_type,
                                content_disposition: 'attachment',
                                public_link: fileInfo.public_link,
                            };
                            EventBus.emit(`richText-${this.hash}:addAttachment`, parsedFile);
                            this.loadingAttachment = true;
                        }
                    })
                    .catch((error) => {
                        const errorMessage = error.response.data.error.message;
                        const errorResponse = {
                            name: fileDetails.name,
                            size: fileDetails.size,
                            error_text: errorMessage,
                            error: true,
                            error_message: errorMessage,
                        };

                        if (typeof errorMessage === 'object') {
                            for (const message of Object.values(errorMessage)) {
                                errorResponse.error_text = message;
                                errorResponse.error_message = message;
                                EventBus.emit(`richText-${this.hash}:addAttachment`, {
                                    ...errorResponse,
                                });
                            }
                        } else {
                            EventBus.emit(`richText-${this.hash}:addAttachment`, {
                                ...errorResponse,
                            });
                        }
                    })
                    .finally(() => {
                        this.$refs.fileInput.value = '';
                        this.loadingAttachment = false;
                    });
            }
        },
        displaySelectFileDropdown() {
            setTimeout(() => {
                this.showAttachmentMenu = false;
                this.showSelectFilesDropdown = true;
                this.searchFilesFromSellsy('');
            }, 0);
        },
        searchFilesFromSellsy(searchTerms) {
            this.isLoadingFiles = true;
            searchFiles(searchTerms, ['attachement', 'email_attachments'])
                .then((response) => {
                    this.files = response.data;

                    if (!this.filesLoadedOnce) {
                        this.filesLoadedOnce = true;

                        if (this.files.length === 0) {
                            this.selectFilesDropdownNoResultsLabel = this.$t.__(
                                'richtext.customoptions.AttachmentRichText.search_files_no_results',
                            );
                        }
                    }
                })
                .catch(() => {
                    this.$pushToast({
                        type: 'error',
                        text: this.$t.__(
                            'richtext.customoptions.AttachmentRichText.search_files_error',
                        ),
                        icon: true,
                    });
                })
                .finally(() => {
                    this.isLoadingFiles = false;
                });
        },
        selectFileFromSellsy(file) {
            this.showSelectFilesDropdown = false;
            this.showAttachmentMenu = false;

            const fileDetails = {
                id: file.id,
                name: file.name,
                size: file.size,
                extension: file.extension,
                content_type: mime.getType(file.extension),
                content_disposition: 'attachment',
                public_link: file.public_link,
            };
            EventBus.emit(`richText-${this.hash}:addAttachment`, fileDetails);
        },
        handleClickOutside($event) {
            if (
                !$event ||
                (!$event.target.closest('.compass-icon-button') &&
                    !$event.target.closest('.compass-menu-item') &&
                    !$event.target.closest(
                        '.attachment-richtext__dropdown .compass-dropdown--filterable__input',
                    ) &&
                    !$event.target.closest(
                        '.attachment-richtext__dropdown .compass-dropdown__footer-button',
                    ))
            ) {
                this.showAttachmentMenu = false;
                this.showSelectFilesDropdown = false;
            }
        },
        getFileIconViewBox(fileExtension) {
            const fileIconInfo = this.getFileIconInfo(fileExtension);
            const currentIcon = icons.find((file) => file.id === fileIconInfo);
            const defaultIcon = icons.find((file) => file.id === 'default');

            return currentIcon ? currentIcon.viewBox : defaultIcon && defaultIcon.viewBox;
        },
        getFileIcon(fileExtension) {
            const fileIconInfo = this.getFileIconInfo(fileExtension);
            const currentIcon = icons.find((file) => file.id === fileIconInfo);
            const defaultIcon = icons.find((file) => file.id === 'default');

            return currentIcon ? currentIcon.src : defaultIcon && defaultIcon.src;
        },
        getFileIconInfo(fileExtension) {
            const fileExtensionFormatted = fileExtension.toLowerCase();
            const isKnownFileType = Object.keys(acceptedFileTypes).includes(fileExtensionFormatted);
            return isKnownFileType ? acceptedFileTypes[fileExtensionFormatted].icon : '';
        },
        getFileName(file) {
            const maxLength = 20;
            if (file && file.name.length > maxLength) {
                return file.name.substring(0, maxLength) + '...' + file.extension;
            }
            return file.name + '.' + file.extension;
        },
        getFileSize(fileSize) {
            const kilobyte = 1024;
            const megabyte = kilobyte * kilobyte;

            if (fileSize < megabyte) {
                return this.$t.__('richtext.customoptions.AttachmentRichText.file_size.kilobytes', {
                    fileSize: (fileSize / kilobyte).toFixed(2),
                });
            } else {
                return this.$t.__('richtext.customoptions.AttachmentRichText.file_size.megabytes', {
                    fileSize: (fileSize / megabyte).toFixed(2),
                });
            }
        },
    },
};
</script>
<style lang="scss" scoped>
.attachment-richtext {
    &__dropdown {
        &-option {
            display: flex;
            align-items: center;
            gap: var(--spacing-1);

            &-icon {
                width: 20px;
                height: 20px;
                flex-shrink: 0;
            }

            &-label {
                color: var(--colors-common-text-standard-default);
                overflow: hidden;
                text-overflow: ellipsis;
                white-space: nowrap;
                max-width: 170px;
            }
        }
    }
}
</style>

<style>
.attachment-richtext__menu,
.attachment-richtext__dropdown {
    z-index: 1000 !important;
}
.attachment-richtext__dropdown .compass-dropdown-option {
    gap: 0;
}
.attachment-richtext__dropdown .compass-dropdown-option .compass-dropdown-option--selected {
    display: none;
}
.attachment-richtext__dropdown .compass-dropdown__content--loading:has(.compass-loader) {
    padding: var(--spacing-1) 0;
}
</style>
